import React, { useRef } from 'react';
import { motion, useInView } from "motion/react";
import type { ContentSection, Business, AgeRequirement, VenueTag } from '@/features/clubs/types/types';
import { getSignedMediaUrl } from '@/lib/utils';
import ImagePlaceholder from '@/features/clubs/components/ImagePlaceholder';
import GoogleMaps from './GoogleMaps';

interface StructuredContentSectionProps {
  sections: ContentSection[];
  clubData?: Business;
}

const StructuredContentSection: React.FC<StructuredContentSectionProps> = ({ sections, clubData }) => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.2,
        duration: 0.6,
        ease: "easeOut" as const
      }
    })
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { delay: 0.3, duration: 0.8, ease: "easeOut" as const }
    }
  };

  const Separator: React.FC = () => (
    <div className="w-full h-px bg-gradient-to-r from-gray-600 via-gray-700 to-transparent"></div>
  );

  const AgeRequirementsDisplay: React.FC<{ ageRequirements: AgeRequirement }> = ({ ageRequirements }) => (
    <div className="space-y-3">
      <Separator />
      <div className="flex items-center gap-4 text-gray-300">
        <span>Entry: {ageRequirements.minimum_entry_age}+</span>
        <span>•</span>
        <span>Drinking: {ageRequirements.drinking_age}+</span>
      </div>
      <p className="text-sm text-gray-400">{ageRequirements.notes}</p>
      <Separator />
    </div>
  );

  const TagsDisplay: React.FC<{ tags: VenueTag[] }> = ({ tags }) => (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((tag, index) => (
        <span
          key={index}
          className="px-2 py-1 bg-gray-700/30 text-gray-300 text-sm rounded-sm hover:bg-gray-600/30 transition-colors"
        >
          {tag}
        </span>
      ))}
    </div>
  );

  const renderSection = (section: ContentSection, index: number) => {
    const isAboutUs = section.section_type === 'about_us';

    // Show images for about_us sections
    const shouldShowImage = section.image_id && section.image_id !== '00000000-0000-0000-0000-000000000000';

    return (
      <motion.section
        key={index}
        ref={index === 0 ? sectionRef : null}
        custom={index}
        variants={textVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        className="py-16"
      >
        <div className="max-w-6xl mx-auto px-8">
          {isAboutUs && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-serif font-medium text-white mb-6">
                  {section.title}
                </h2>
                <div className="text-gray-300 leading-relaxed space-y-4">
                  {section.body.split('\n\n').map((paragraph, pIndex) => (
                    <p key={pIndex}>{paragraph}</p>
                  ))}
                </div>
                
                {/* Compact Map Section */}
                {clubData && (
                  <div className="mt-8">
                    <div className="bg-gray-900/30 backdrop-blur-sm border border-gray-800/50 rounded-xl overflow-hidden">
                      {/* Location Text */}
                      <div className="p-4 border-b border-gray-800/50">
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 bg-gray-700/50 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-lg font-medium text-white">{clubData.name}</h4>
                            <p className="text-gray-400 text-sm">
                              {clubData.location.address_line1}, {clubData.location.city}, {clubData.location.country}
                            </p>
                          </div>
                        </div>
                      </div>
                      {/* Map */}
                      <GoogleMaps 
                        location={clubData.location} 
                        className="h-48 w-full"
                      />
                    </div>
                  </div>
                )}
                
                {/* Age Requirements and Tags */}
                {clubData && (
                  <div className="mt-8">
                    {clubData.age_requirements && (
                      <div className="mb-6">
                        <AgeRequirementsDisplay ageRequirements={clubData.age_requirements} />
                      </div>
                    )}
                    {clubData.tags && clubData.tags.length > 0 && (
                      <div className="mb-6">
                        <TagsDisplay tags={clubData.tags} />
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Circular Image and Action Buttons Container */}
              <div className="flex flex-col items-center gap-6">
                {shouldShowImage && (
                  <motion.div
                    variants={imageVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    className="relative w-[28rem] h-[28rem] rounded-full overflow-hidden mx-auto flex items-center justify-center"
                  >
                    <img
                      src={getSignedMediaUrl(section.image_id, 'image').url}
                      alt={section.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    <ImagePlaceholder label={section.title} className="hidden" />
                  </motion.div>
                )}
              </div>
            </div>
          )}
        </div>
      </motion.section>
    );
  };

  if (!sections || sections.length === 0) {
    return null;
  }

  return (
    <div className="bg-black">
      {sections.map((section, index) => renderSection(section, index))}
    </div>
  );
};

export default StructuredContentSection;
